import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AcademicCapIcon, LightBulbIcon, MapIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { CAREER_PASSPORTS } from '../config/careerPassportAssets';
import './About.css';

const planGoals = [
  { title: 'Skills and learning pathways', description: 'Develop the Skills Gauntlet and Skills Passports, combining practical expertise, transferable skills and the values needed for work and community life.' },
  { title: 'Public employment and livelihoods', description: 'Connect young people outside employment, education or training with learning and work experience that helps address local infrastructure needs.' },
  { title: 'Learning closer to communities', description: 'Develop the Skillix network of local skills campuses, bringing workshops, digital learning and enterprise support closer to residents.' },
  { title: 'Education technology and manufacturing', description: 'Build the Gauteng City-Region Educational Technology and Simulation Manufacturing Cluster to support innovation and practical learning.' },
  { title: 'A capable public service', description: 'Develop the leadership, adaptability and specialist capabilities that public servants need to improve delivery and respond to changing demands.' },
  { title: 'Partnerships and coordinated delivery', description: 'Align government, education and training institutions, employers and funding partners around shared skills priorities and evidence of results.' }
];

const belts = [
  { name: 'White', color: '#ffffff' }, { name: 'Yellow', color: '#f5ce32' },
  { name: 'Orange', color: '#f28c28' }, { name: 'Green', color: '#27845b' },
  { name: 'Blue', color: '#2862b4' }, { name: 'Purple', color: '#8152ac' },
  { name: 'Brown', color: '#855536' }, { name: 'Black', color: '#202833' }
];

const learnerSteps = [
  { icon: LightBulbIcon, title: 'Understand your strengths', description: 'Build your profile and explore your skills and interests through the career assessment.' },
  { icon: MapIcon, title: 'Explore your pathways', description: 'See which passport tracks connect with your interests and recommended training.' },
  { icon: BookOpenIcon, title: 'Choose your learning', description: 'Review course requirements and passport targets, then apply or start a linked LMS course.' },
  { icon: AcademicCapIcon, title: 'Follow your progress', description: 'Return to your dashboard to manage applications, continue learning and view passport progress.' }
];

const About = () => (
  <div className="about-page">
    <section className="about-intro" aria-labelledby="about-title">
      <div className="about-container">
        <h1 id="about-title">About GMSP3<br />Career Guidance</h1>
        <p className="about-intro-tagline">Find your direction. Build your skills. Shape your future.</p>
        <p className="about-intro-description">
          A place to explore your strengths, discover Skills Passport pathways and find training
          for your next step in work, enterprise or community life.
        </p>
        <div className="about-actions">
          <a href="#skills-passports" className="about-button about-button--primary">Explore the passports<ArrowRightIcon aria-hidden="true" /></a>
          <a href="#gmsp3" className="about-button about-button--secondary">Understand GMSP3</a>
        </div>
      </div>
    </section>

    <section className="about-plan" id="gmsp3" aria-labelledby="about-plan-title">
      <div className="about-container">
        <div className="about-plan-overview">
          <div className="about-section-heading">
            <h2 id="about-plan-title">The Gauteng Master Skills Plan 3</h2>
            <p>A coordinated skills vision for the Gauteng City-Region, 2026–2030.</p>
          </div>
          <div className="about-plan-copy">
            <p>
              The Gauteng Master Skills Plan 3 (GMSP3), prepared by the Gauteng Office of the Premier,
              sets out a coordinated approach to developing the skills that Gauteng’s economy and
              communities need. It connects education and training with employment, entrepreneurship,
              infrastructure renewal and stronger public services.
            </p>
            <p>
              The plan starts with demand: what skills do employers, communities and public institutions
              actually need? It brings government, education and training institutions, and the private
              sector together to shape learning pathways around those needs. A particular focus is young
              people who are not in employment, education or training, alongside support for small
              enterprises and the province’s wider skills pipeline.
            </p>
            <p>
              At its centre is the <strong>GCR Triple Helix Skills Gauntlet</strong>:
              an approach that develops generic, transversal and technical skills together.
              Practical expertise is supported by communication, problem-solving, adaptability,
              digital literacy, ethics and civic participation. The six Skills Passports make these
              learning pathways easier to understand and organise.
            </p>
          </div>
        </div>

        <div className="about-plan-target">
          <strong>500,000</strong>
          <div><h3>Learning opportunities targeted for 2026–2030</h3>
            <p>The supplied GMSP3 draft sets this ambition across a coordinated skills ecosystem,
              supported by digital learning, partnerships and measurable employment and enterprise outcomes.</p>
          </div>
        </div>

        <div className="about-section-heading about-goals-heading">
          <h3>Six goals that turn the plan into action</h3>
          <p>From learning pathways to local delivery and lasting partnerships.</p>
        </div>
        <ol className="about-goals">
          {planGoals.map((goal, index) => (
            <li key={goal.title}><span className="about-goal-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <div><h4>{goal.title}</h4><p>{goal.description}</p></div>
            </li>
          ))}
        </ol>
        <p className="about-source">Plan overview: GMSP3 final draft dated 22 July 2026, particularly the learning pathways and theory of change sections.</p>
      </div>
    </section>

    <section className="about-passports" id="skills-passports" aria-labelledby="about-passports-title">
      <div className="about-container">
        <div className="about-section-heading">
          <h2 id="about-passports-title">Six passports. Many ways forward.</h2>
          <p>Each passport represents a domain of skills. You can explore more than one pathway
            and build a combination that fits your interests and goals.</p>
        </div>
        <div className="about-passport-grid">
          {CAREER_PASSPORTS.map(passport => (
            <article key={passport.id} className={`about-passport about-passport--${passport.tone}`} aria-labelledby={`about-passport-${passport.id}`}>
              <div className="about-passport-cover" aria-hidden="true">
                <img src={passport.cover} alt="" width="510" height="702" loading="lazy" decoding="async" />
                <div className="about-passport-cover-copy"><span>SKILLS PASSPORT</span><strong>{passport.title}</strong><small>{passport.nqfLevels}</small></div>
              </div>
              <div className="about-passport-body">
                <h3 id={`about-passport-${passport.id}`}>{passport.title}</h3>
                <p>{passport.description}</p>
                <div className="about-passport-coverage"><h4>What it covers</h4><p>{passport.coverage}</p></div>
                <div className="about-passport-range"><AcademicCapIcon aria-hidden="true" /><span>{passport.nqfLevels}</span></div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="about-progression" aria-labelledby="about-progression-title">
      <div className="about-container">
        <div className="about-section-heading">
          <h2 id="about-progression-title">Understanding your passport progression</h2>
          <p>Your passport track, standard and belt describe different parts of your skills journey.</p>
        </div>
        <div className="about-progression-grid">
          <div className="about-progression-copy">
            <dl className="about-progression-definitions">
              <div><dt>Passport track</dt><dd>The skills domain you are developing, such as Digital or Green. You can build progress in several tracks.</dd></div>
              <div><dt>Standard</dt><dd>The named stage recorded for that pathway: <strong>First Standard</strong>, <strong>Second Standard</strong> or <strong>Third Standard</strong>.</dd></div>
              <div><dt>Belt</dt><dd>The competency level recorded within the passport pathway, using the eight belt colours below.</dd></div>
            </dl>
            <p className="about-progression-note">
              NQF levels describe qualifications; belts and standards describe passport progression.
              A course’s mapped target shows the pathway it supports. A verified passport award depends
              on meeting the relevant requirements and assessment criteria.
            </p>
          </div>
          <aside className="about-example" aria-labelledby="about-example-title">
            <span className="about-example-label">An example passport record</span>
            <h3 id="about-example-title">Green Passport</h3>
            <div className="about-example-levels"><span>First Standard</span><span className="about-example-belt">Blue Belt</span></div>
            <p>This identifies the Green skills pathway, with First Standard and Blue Belt recorded
              as its progression. It does not mean NQF Level 1 or an automatic qualification award.</p>
            <p className="about-example-caption">Illustrative example, not an awarded learner credential.</p>
          </aside>
        </div>
        <div className="about-belt-section">
          <h3>The eight belt levels</h3>
          <ol className="about-belts" aria-label="Belt progression from White to Black">
            {belts.map(belt => <li key={belt.name}><span className="about-belt-swatch" style={{ backgroundColor: belt.color }} aria-hidden="true" /><span>{belt.name}</span></li>)}
          </ol>
          <p>White → Yellow → Orange → Green → Blue → Purple → Brown → Black</p>
        </div>
      </div>
    </section>

    <section className="about-journey" aria-labelledby="about-journey-title">
      <div className="about-container">
        <div className="about-section-heading"><h2 id="about-journey-title">How the app helps you take the next step</h2>
          <p>Career guidance connects your interests with training. Your dashboard brings the journey together.</p></div>
        <ol className="about-journey-steps">
          {learnerSteps.map(({ icon: Icon, title, description }) => <li key={title}><Icon aria-hidden="true" /><h3>{title}</h3><p>{description}</p></li>)}
        </ol>
      </div>
    </section>

    <section className="about-start" aria-labelledby="about-start-title">
      <div className="about-container about-start-content"><div><h2 id="about-start-title">Your next pathway starts with you.</h2>
        <p>Explore your interests or browse training programs to see where you could go next.</p></div>
        <div className="about-actions"><Link to="/career" className="about-button about-button--primary">Find my direction<ArrowRightIcon aria-hidden="true" /></Link>
          <Link to="/courses" className="about-button about-button--secondary">Browse programs</Link></div>
      </div>
    </section>
  </div>
);

export default About;
